import { Modal } from "@heroui/react";
import { createContext, useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/src/common/components/ui/button";

export interface ConfirmOptions {
	title: string;
	description: string;
	confirmLabel?: string;
	cancelLabel?: string;
}

export interface ConfirmDialogContextValue {
	confirm: (options: ConfirmOptions) => Promise<boolean>;
}

export const ConfirmDialogContext = createContext<ConfirmDialogContextValue | null>(null);

export function ConfirmDialogProvider({ children }: { children: React.ReactNode }) {
	const { t } = useTranslation("common");
	const [isOpen, setIsOpen] = useState(false);
	const [options, setOptions] = useState<ConfirmOptions | null>(null);
	const resolverRef = useRef<((value: boolean) => void) | null>(null);

	const close = useCallback((result: boolean) => {
		resolverRef.current?.(result);
		resolverRef.current = null;
		setIsOpen(false);
	}, []);

	const confirm = useCallback((next: ConfirmOptions) => {
		setOptions(next);
		setIsOpen(true);
		return new Promise<boolean>((resolve) => {
			resolverRef.current = resolve;
		});
	}, []);

	const value = useMemo<ConfirmDialogContextValue>(() => ({ confirm }), [confirm]);

	return (
		<ConfirmDialogContext.Provider value={value}>
			{children}
			<Modal isOpen={isOpen} onOpenChange={(open) => !open && close(false)}>
				<Modal.Backdrop>
					<Modal.Container>
						<Modal.Dialog>
							<Modal.Header>
								<Modal.Heading>{options?.title}</Modal.Heading>
								<Modal.CloseTrigger />
							</Modal.Header>
							<Modal.Body className="p-6">
								<p className="text-sm text-default-600">{options?.description}</p>
							</Modal.Body>
							<Modal.Footer>
								<Button variant="outline" onPress={() => close(false)}>
									{options?.cancelLabel ?? t("confirm.cancel")}
								</Button>
								<Button variant="danger" onPress={() => close(true)}>
									{options?.confirmLabel ?? t("confirm.delete")}
								</Button>
							</Modal.Footer>
						</Modal.Dialog>
					</Modal.Container>
				</Modal.Backdrop>
			</Modal>
		</ConfirmDialogContext.Provider>
	);
}
